import { bearing, centroid, lineString, midpoint } from '@turf/turf';
import { watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { getMode, setShapeId } from 'shared/shapes/domain/shapeGeoJson';
import { useMapStyleStore } from 'shared/mapStyles/stores/mapStyleStore';
import type {
  LogYardContext,
  BulkStorageContext,
  ProductContext,
  ProductGroupContext,
  ShapeContext,
  StorageAreaContext,
  StorageAreaStatuses,
  StorageAreaTypes,
} from 'shared/mapStyles/domain/shapeContext';
import { useRafFn } from '@vueuse/core';
import { ShapeMode } from 'shared/shapes/domain/shapeMode';
import { isBulkStorage, isLogYard, isLogYardPile, StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { calculateTextColor } from 'shared/color';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { useLanguageStore } from '../../translations/stores/languageStore';
import { useProductGroupStore } from '../../products/groups/stores/productGroupStore';
import { useProductStore } from '../../products/stores/productStore';
import type { Product } from '../../products/domain/product';
import type { Shape } from '../../shapes/domain/shape';
import { useShapeStore } from '../../shapes/stores/shapeStore';
import { useStorageAreaStore } from '../../storageAreas/stores/storageAreaStore';
import type {
  BulkContent,
  BulkStorageData,
  LogYardContent,
  LogYardData,
  StorageArea,
} from '../../storageAreas/domain/storageArea';
import { useCoilStore } from '../../coils/stores/coilStore';
import type { MapStyle } from '../../mapStyles/domain/mapStyle';
import { useProductionOrderStore } from '../../productionOrders/stores/productionOrderStore';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type { Geometry } from 'ol/geom';
import type Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON.js';
import { rectangleStyle } from '../styles/rectangleStyle';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';
import { Color } from '../../common/domain/color';
import { getDaysSinceDate } from '../../common/date';
import { usePreviewRectangleLayer } from './previewRectangleLayer';
import { usePreviewPolygonLayer } from './previewPolygonLayer';
import { parseRichText } from '../../mapStyles/domain/richText';

const storageAreaTypes: StorageAreaTypes = {
  producer: StorageAreaType.Producer,
  consumer: StorageAreaType.Consumer,
  vehicle: StorageAreaType.Vehicle,
  logSorterIntake: StorageAreaType.LogSorterIntake,
  sawLineIntake: StorageAreaType.SawLineIntake,
  logSorterBin: StorageAreaType.LogSorterBin,
  sortedPile: StorageAreaType.SortedPile,
  unsortedPile: StorageAreaType.UnsortedPile,
  truck: StorageAreaType.Truck,
  train: StorageAreaType.Train,
  bulk: StorageAreaType.Bulk,
};

const storageAreaStatuses: StorageAreaStatuses = {
  undefined: StorageAreaStatus.Undefined,
  empty: StorageAreaStatus.Empty,
  inuse: StorageAreaStatus.InUse,
  full: StorageAreaStatus.Full,
};

export interface ShapeLayerClickEvent {
  shapeId: string | null;
  storageAreaId: string | null;
  multiple: boolean;
}

export type ShapeLayerClickListener = (ev: ShapeLayerClickEvent) => void;

export interface ShapeLayerOptions {
  mapStyle?: Ref<MapStyle>;
}

export function useShapeLayer(options?: ShapeLayerOptions) {
  const mapStyleStore = useMapStyleStore();
  const languageStore = useLanguageStore();
  const productGroupStore = useProductGroupStore();
  const productStore = useProductStore();
  const shapeStore = useShapeStore();
  const storageAreaStore = useStorageAreaStore();
  const coilStore = useCoilStore();
  const productionOrderStore = useProductionOrderStore();

  const geojson = new GeoJSON();

  let onClickCallback: ShapeLayerClickListener = (ev) => {};

  let renderScheduled = true;

  const selectedStyle = getMapStyle();

  const viewModels = new Map<string, ShapeViewModel>();

  const hiddenShapeIds = new Set<string>();
  const hiddenStorageAreaIds = new Set<string>();

  let searchActive: boolean = false;
  const searchedStorageAreaIds = new Set<string>();

  const selectedStorageAreaIds = new Set<string>();

  let vehicleLogYardContent: LogYardContent[] | null = null;
  let vehicleBulkContent: BulkContent[] | null = null;

  let insideStorageAreaId: string | null = null;

  shapeStore.changed.on((ev) => {
    if (ev.action === 'removed') {
      deleteShape(ev.id);
    } else {
      mergeShape(ev.data);
    }
  });

  for (const shape of shapeStore.shapes) {
    mergeShape(shape);
  }

  watch(productStore, () => scheduleRenderAll());
  watch(productGroupStore, () => scheduleRenderAll());
  watch(storageAreaStore, () => scheduleRenderAll());
  watch(coilStore, () => scheduleRenderAll());

  return {
    attach,
    hideShapes,
    hideStorageArea,
    addClickListener,
    selectStorageArea,
    deselectStorageArea,
    setVehicleLogYardContent,
    setVehicleBulkContent,
    insideStorageArea,
    searchStorageAreas,
    clearSearchStorageAreas,
  };

  function getMapStyle() {
    if (options && options.mapStyle) return options.mapStyle;
    const { selectedStyle } = storeToRefs(mapStyleStore);
    return selectedStyle;
  }

  function setVehicleLogYardContent(logYardContent: LogYardContent[] | null) {
    vehicleLogYardContent = logYardContent;
    scheduleRenderAll();
  }

  function setVehicleBulkContent(bulkContent: BulkContent[] | null) {
    vehicleBulkContent = bulkContent;
    scheduleRenderAll();
  }

  function insideStorageArea(storageAreaId: string | null) {
    if (insideStorageAreaId !== null) {
      scheduleRenderStorageArea(insideStorageAreaId);
    }

    insideStorageAreaId = storageAreaId;

    if (storageAreaId !== null) {
      scheduleRenderStorageArea(storageAreaId);
    }
  }

  function selectStorageArea(id: string) {
    scheduleRenderStorageAreas(selectedStorageAreaIds);

    selectedStorageAreaIds.clear();
    selectedStorageAreaIds.add(id);

    scheduleRenderStorageAreas(selectedStorageAreaIds);
  }

  function deselectStorageArea() {
    scheduleRenderStorageAreas(selectedStorageAreaIds);
    selectedStorageAreaIds.clear();
  }

  function scheduleRenderStorageAreas(ids: Set<string>) {
    for (const id of ids) {
      scheduleRenderStorageArea(id);
    }
  }

  function scheduleRenderShapes(ids: Set<string>) {
    for (const id of ids) {
      scheduleRenderShape(id);
    }
  }

  function scheduleRenderShape(id: string) {
    const viewModel = viewModels.get(id);
    if (!viewModel) return;
    viewModel.renderScheduled = true;
    renderScheduled = true;
  }

  function scheduleRenderStorageArea(id: string) {
    const shapes = shapeStore.getByStorageAreaId(id);
    if (!shapes) return;
    for (const shape of shapes) {
      const viewModel = viewModels.get(shape.id);
      if (!viewModel) continue;
      viewModel.renderScheduled = true;
      renderScheduled = true;
    }
  }

  function addClickListener(listener: ShapeLayerClickListener) {
    onClickCallback = listener;
  }

  function hideStorageArea(action: (ids: Set<string>) => void) {
    scheduleRenderStorageAreas(hiddenStorageAreaIds);
    action(hiddenStorageAreaIds);
    scheduleRenderStorageAreas(hiddenStorageAreaIds);
  }

  function hideShapes(action: (ids: Set<string>) => void) {
    scheduleRenderShapes(hiddenShapeIds);
    action(hiddenShapeIds);
    scheduleRenderShapes(hiddenShapeIds);
  }

  function searchStorageAreas(action: (ids: Set<string>) => void) {
    searchActive = true;
    action(searchedStorageAreaIds);
    scheduleRenderAll();
  }

  function clearSearchStorageAreas() {
    searchActive = false;
    searchedStorageAreaIds.clear();
    scheduleRenderAll();
  }

  function mergeShape(shape: Shape) {
    let viewModel = viewModels.get(shape.id);

    if (viewModel) {
      viewModel.shape = shape;
    } else {
      viewModel = {
        id: shape.id,
        deleteScheduled: false,
        shape: shape,
        feature: null,
        renderScheduled: true,
      };
      viewModels.set(viewModel.id, viewModel);
    }

    viewModel.renderScheduled = true;
    renderScheduled = true;
  }

  function deleteShape(id: string) {
    const viewModel = viewModels.get(id);
    if (!viewModel) return;
    viewModel.deleteScheduled = true;
    renderScheduled = true;
  }

  function scheduleRenderAll() {
    for (const viewModel of viewModels.values()) {
      scheduleRender(viewModel);
    }
  }

  function sameProductsAsVehicle(storageArea: StorageArea) {
    const storageAreaProducts = getStorageAreaProducts(storageArea);
    const vehicleProducts = getVehicleProducts();

    if (!vehicleProducts || vehicleProducts.size === 0) {
      return false;
    }

    if (storageArea.status === StorageAreaStatus.Full) {
      // If the storage area is full then it should never be suggested as having the same product as the vehicle
      // even if it has the same products.
      return false;
    }

    let matchingProducts = 0;
    for (const vehicleProduct of vehicleProducts.values()) {
      if (storageAreaProducts.has(vehicleProduct.id)) {
        matchingProducts++;
      }
    }

    return matchingProducts > 0;
  }

  function sameProductAsAllSawLineIntake(storageArea: StorageArea): boolean {
    const storageAreaProducts = getStorageAreaProducts(storageArea);

    const intakes = storageAreaStore.sawLineIntakes;

    for (const intake of intakes) {
      if (storageArea.id === intake.id) {
        continue;
      }
      const intakeProducts = getStorageAreaAllowedProducts(intake);

      for (const product of storageAreaProducts.values()) {
        if (intakeProducts.has(product.id)) {
          return true;
        }
      }
    }

    return false;
  }

  function sameProductAsSawLineIntake(storageArea: StorageArea): (sawlineName: string) => boolean {
    return (sawlineName: string) => {
      const storageAreaProducts = getStorageAreaProducts(storageArea);

      const intakes = storageAreaStore.sawLineIntakes;

      for (const intake of intakes) {
        if (intake.name !== sawlineName) continue;

        if (storageArea.id === intake.id) {
          continue;
        }
        const intakeProducts = getStorageAreaAllowedProducts(intake);

        for (const product of storageAreaProducts.values()) {
          if (intakeProducts.has(product.id)) {
            return true;
          }
        }
      }

      return false;
    };
  }

  function sameProductAsLogSorterProductionOrder(
    storageArea: StorageArea,
    logYard: LogYardData,
  ): (logSorterName: string) => boolean {
    return (logSorterName: string) => {
      const intakes = storageAreaStore.storageAreas.filter(
        (s) => s.type === StorageAreaType.LogSorterIntake,
      );
      const products: Map<string, Product> = new Map<string, Product>();
      for (const intake of intakes) {
        if (logSorterName && intake.name !== logSorterName) {
          continue;
        }

        if (!intake.orderName) {
          continue;
        }

        const productionOrder = productionOrderStore.productionOrders.filter(
          (po) => po.name === intake.orderName,
        )[0];

        if (productionOrder) {
          for (const recipe of productionOrder.recipes) {
            for (const outProduct of recipe.outProducts) {
              const product = productStore.getById(outProduct.productId);
              if (product) {
                products.set(product.id, product);
              }
            }
          }
        }
      }

      // Fetch fresh storage area data
      const currentStorageArea = storageAreaStore.getById(storageArea.id);
      if (!currentStorageArea) return false;

      const storageAreaProducts = getStorageAreaProducts(currentStorageArea);
      for (const product of storageAreaProducts.values()) {
        if (products.has(product.id)) {
          return true;
        }
      }

      const allowedProducts = getStorageAreaAllowedProducts(currentStorageArea);
      for (const product of allowedProducts.values()) {
        if (products.has(product.id)) {
          return true;
        }
      }

      return false;
    };
  }

  function sameProductAsLogSorterBin(storageArea: StorageArea): boolean {
    if (storageArea.type === StorageAreaType.LogSorterBin) {
      return false;
    }

    const storageAreaProducts = getStorageAreaProducts(storageArea);

    const bins = storageAreaStore.logSorterBins;

    for (const bin of bins) {
      if (storageArea.id === bin.id) {
        continue;
      }

      const binProducts = getStorageAreaProducts(bin);

      for (const product of storageAreaProducts.values()) {
        if (binProducts.has(product.id)) {
          return true;
        }
      }
    }

    return false;
  }

  function productExistsInOtherStorageAreas(storageArea: StorageArea): boolean {
    const storageAreaProducts = getStorageAreaProducts(storageArea);

    if (storageAreaProducts.size === 0) {
      return true;
    }

    for (const otherStorageArea of storageAreaStore.storageAreas.values()) {
      if (storageArea.id === otherStorageArea.id) {
        continue;
      }

      if (otherStorageArea.status === StorageAreaStatus.Full) {
        continue;
      }

      if (otherStorageArea.type === StorageAreaType.LogSorterBin) {
        continue;
      }

      if (!otherStorageArea.active) {
        continue;
      }

      const otherStorageAreaProducts = getStorageAreaProducts(otherStorageArea);
      const otherStorageAreaAllowedProducts = getStorageAreaAllowedProducts(otherStorageArea);

      for (const product of storageAreaProducts.values()) {
        if (otherStorageAreaProducts.has(product.id)) {
          return true;
        }

        if (otherStorageAreaAllowedProducts.has(product.id)) {
          return true;
        }
      }
    }

    return false;
  }

  function calculateFillLevelVolume(storageArea: StorageArea): number | null {
    if(isLogYard(storageArea.type)) {
      return calculateLogYardFillLevel(storageArea);
    };
    if(isBulkStorage(storageArea.type)) {
      return calculateBulkFillLevelVolume(storageArea);
    }
    return null;
  }

    function calculateFillLevelWeight(storageArea: StorageArea): number | null {
     if(isLogYard(storageArea.type)) {
      return null;
     }
    if(isBulkStorage(storageArea.type)) {
      return calculateBulkFillLevelWeight(storageArea);
    }
    return null;
  }

  function calculateLogYardFillLevel(storageArea: StorageArea): number {
    if (storageArea.logYardData) {
      const logYardContents = storageArea.logYardData.content;
      let filledSideArea = 0.0;
      for (const content of logYardContents) {
        const product = productStore.getById(content.productId);
        if (product?.logData?.logsPerSquareMeter && product.logData.logsPerSquareMeter > 0) {
          filledSideArea += content.numberOfLogs / product?.logData.logsPerSquareMeter;
        }
      }

      if (storageArea.logYardData.sideArea && storageArea.logYardData.sideArea > 0) {
        return Math.round(
          Math.min((filledSideArea / storageArea.logYardData.sideArea) * 100.0, 100),
        );
      }
    }

    return 0;
  }

  function calculateBulkFillLevelWeight(storageArea: StorageArea): number {
    const bulkStorageData = storageArea.bulkStorageData;

    if (!bulkStorageData?.maxWeight || bulkStorageData.maxWeight <= 0) {
      return 0;
    }

    const filledWeight = bulkStorageData.content.reduce((sum, content) => sum + content.ton, 0);

    return Math.round(Math.min((filledWeight / bulkStorageData.maxWeight) * 100, 100));
  }

function calculateBulkFillLevelVolume(storageArea: StorageArea): number {
  const bulkStorageData = storageArea.bulkStorageData;
  if (!bulkStorageData?.maxVolume || bulkStorageData.maxVolume <= 0) {
    return 0;
  }

  let filledVolume = 0;

  for (const content of bulkStorageData.content) {
    const product = productStore.getById(content.productId);
    const density = product?.bulkData?.density;

    if (density && density > 0) {
      const massKg = content.ton * 1000; // tons → kg
      filledVolume += massKg / density; // m³
    }
  }

  return Math.round(
    Math.min((filledVolume / bulkStorageData.maxVolume) * 100, 100),
  );
}

  function getVehicleProducts(): Map<string, Product> {
    const products = new Map<string, Product>();

    if (vehicleLogYardContent) {
      for (const [key, product] of getLogYardProducts(vehicleLogYardContent)) {
        products.set(key, product);
      }
    }

    if (vehicleBulkContent) {
      for (const [key, product] of getBulkProducts(vehicleBulkContent)) {
        products.set(key, product);
      }
    }

    return products;
  }


function getStorageAreaProducts(storageArea: StorageArea): Map<string, Product> {
  const products = new Map<string, Product>();

  if (storageArea.logYardData) {
    for (const [key, product] of getLogYardProducts(storageArea.logYardData.content)) {
      products.set(key, product);
    }
  }

  if (storageArea.bulkStorageData) {
    for (const [key, product] of getBulkProducts(storageArea.bulkStorageData.content)) {
      products.set(key, product);
    }
  }

  return products;
}

  function getLogYardProducts(contents: LogYardContent[]) {
    const products = new Map<string, Product>();

    for (const content of contents) {
      const product = productStore.getById(content.productId);
      if (!product) continue;
      products.set(product.id, product);
    }

    return products;
  }

    function getBulkProducts(contents: BulkContent[]) {
    const products = new Map<string, Product>();

    for (const content of contents) {
      const product = productStore.getById(content.productId);
      if (!product) continue;
      products.set(product.id, product);
    }

    return products;
  }

  function getStorageAreaAllowedProducts(storageArea: StorageArea) {
    if (storageArea.logYardData) {
      return getLogYardAllowedProducts(storageArea.logYardData.allowedProducts);
    } else {
      return new Map<string, Product>();
    }
  }

  function getLogYardAllowedProducts(productIds: string[]) {
    const products = new Map<string, Product>();

    for (const productId of productIds) {
      const product = productStore.getById(productId);
      if (!product) continue;
      products.set(product.id, product);
    }

    return products;
  }

  function allowedToPlaceVehicleContentInLogYard(logYard: LogYardData) {
    if (!vehicleLogYardContent || vehicleLogYardContent.length === 0) return false;

    const allowedProducts = logYard.allowedProducts;

    for (const content of vehicleLogYardContent) {
      if (!allowedProducts.some((productId) => content.productId === productId)) {
        return false;
      }
    }

    return true;
  }

    function allowedToPlaceVehicleContentInBulkStorage(bulkStorage: BulkStorageData) {
    if (!vehicleBulkContent || vehicleBulkContent.length === 0) return false;

    const allowedProducts = bulkStorage.allowedProducts;

    for (const content of vehicleBulkContent) {
      if (!allowedProducts.some((productId) => content.productId === productId)) {
        return false;
      }
    }

    return true;
  }

  function scheduleRender(viewModel: ShapeViewModel) {
    viewModel.renderScheduled = true;
    renderScheduled = true;
  }

  function attach(map: OLMap) {
    const { source } = createLayer(map);

    useRafFn(() => {
      if (!renderScheduled) return;

      for (const viewModel of viewModels.values()) {
        render(source, viewModel);
      }

      renderScheduled = false;
    });

    watch(selectedStyle, () => {
      scheduleRenderAll();
    });

    map.on('click', async (ev) => {
      if (ev.originalEvent.altKey) return;
      if (ev.defaultPrevented) return;

      const features = source.getFeaturesAtCoordinate(ev.coordinate);
      if (features.length === 0) {
        map.getAllLayers().forEach((layer) => {
          if (
            layer.get('name') === 'previewRectangleLayer' ||
            layer.get('name') === 'previewPolygonLayer'
          ) {
            const layerSource = layer.getSource() as VectorSource<Feature<Geometry>>;
            const previewFeatures = layerSource?.getFeaturesAtCoordinate(ev.coordinate);
            if (previewFeatures && previewFeatures.length > 0) {
              features.push(...previewFeatures);
            }
          }
        });
      }

      for (const feature of features) {
        const shapeId = feature.getId();
        if (shapeId === undefined) continue;
        if (typeof shapeId === 'number') continue;
        const viewModel = viewModels.get(shapeId);
        if (!viewModel) continue;

        onClickCallback({
          shapeId,
          storageAreaId: viewModel.shape.storageAreaId,
          multiple: ev.originalEvent.ctrlKey || ev.originalEvent.metaKey,
        });

        return;
      }

      onClickCallback({
        shapeId: null,
        storageAreaId: null,
        multiple: ev.originalEvent.ctrlKey || ev.originalEvent.metaKey,
      });
    });

    return source;

    function render(source: VectorSource<Feature<Geometry>>, viewModel: ShapeViewModel) {
      const storageAreaId = viewModel.shape.storageAreaId;
      if (
        viewModel.deleteScheduled ||
        hiddenShapeIds.has(viewModel.id) ||
        (storageAreaId !== null && hiddenStorageAreaIds.has(storageAreaId))
      ) {
        if (viewModel.feature) {
          source.removeFeature(viewModel.feature);
          viewModel.feature = null;
        }

        if (viewModel.deleteScheduled) {
          viewModels.delete(viewModel.id);
        }
        return;
      }

      if (!viewModel.renderScheduled) return;

      const shape = viewModel.shape;
      const shapeFeature = shape?.shapeGeoJson;
      const shapeGeometry = shapeFeature?.geometry;
      let feature = viewModel.feature;

      if (!shape || !shapeGeometry) {
        if (feature) {
          source.removeFeature(feature);
          viewModel.feature = null;
        }

        return;
      }

      if (feature) {
        feature.setGeometry(geojson.readGeometry(shapeGeometry));
      } else {
        shapeFeature.id = shape.id;
        const newFeature = shapeFeature;
        setShapeId(newFeature, shape.id);
        feature = geojson.readFeature(newFeature) as Feature<Geometry>;
        source.addFeature(feature);
        viewModel.feature = feature;
      }

      let storageArea: StorageArea | undefined;
      let selected = false;

      if (storageAreaId) {
        storageArea = storageAreaStore.getById(storageAreaId);
        selected = selectedStorageAreaIds.has(storageAreaId);
      }

      let newColor = '#fafafa';
      let newOutlineColor = '#000000';
      let newOutlineSize = 1;
      let newLabel = '';
      let newOpacity = 0.8;
      let newFontSize = 12;
      let messageIconVisible = false;

      if (storageArea && searchActive && !searchedStorageAreaIds.has(storageArea.id)) {
        newOpacity = 0.2;
      }

      const shapeStyle = selectedStyle.value?.shape;

      if (shapeStyle) {
        let productContext: ProductContext | null = null;
        let storageAreaContext: StorageAreaContext | null = null;
        let logYardContext: LogYardContext | null = null;
        let bulkStorageContext: BulkStorageContext | null = null;
        let productGroupContext: ProductGroupContext | null = null;

        if (storageArea) {
          storageAreaContext = {
            name: storageArea.name,
            status: storageArea.status,
            type: storageArea.type,
            allowedToPlace: false,
            sameProductsAsVehicle: sameProductsAsVehicle(storageArea),
            empty: true,
            id: storageArea.id,
            note: storageArea.note ?? '',
            importantNote: storageArea.importantNote,
            sameProductAsSawLineIntake: sameProductAsAllSawLineIntake(storageArea),
            sameProductAsSawLineIntakeFunc: sameProductAsSawLineIntake(storageArea),
            sameProductAsLogSorterBin: sameProductAsLogSorterBin(storageArea),
            productExistsInOtherStorageAreas: productExistsInOtherStorageAreas(storageArea),
            fillLevel: calculateFillLevelVolume(storageArea), // Depricated by fillLevelVolume, remove when all customers have changed to fillLevelVolume.
            fillLevelVolume: calculateFillLevelVolume(storageArea),
            fillLevelWeight: calculateFillLevelWeight(storageArea),
          };

          const productIds = new Set<string>();

          const logYardData = storageArea.logYardData;

          if (logYardData) {
            storageAreaContext.allowedToPlace = allowedToPlaceVehicleContentInLogYard(logYardData);

            for (const content of logYardData.content) {
              productIds.add(content.productId);
            }
          }

          const coils = coilStore.getByStorageAreaId(storageArea.id);

          for (const coil of coils) {
            productIds.add(coil.productId);
          }

          const bulkStorageData = storageArea.bulkStorageData;

          if (bulkStorageData) {
            storageAreaContext.allowedToPlace = allowedToPlaceVehicleContentInBulkStorage(bulkStorageData);

            for (const content of bulkStorageData.content) {
              productIds.add(content.productId);
            }
          }

          const products: Product[] = [];

          for (const productId of productIds) {
            const product = productStore.getById(productId);
            if (!product) continue;
            products.push(product);
          }

          let nameString = '';
          let allowedProductsString: string | null = null;

          if (products.length > 0) {
            products.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

            storageAreaContext.empty = false;

            const firstProduct = products[0];
            if (firstProduct) {
              const productGroup = productGroupStore.getById(firstProduct.productGroupId);

              let color = firstProduct.color ?? productGroup?.color ?? null;

              productContext = {
                id: firstProduct.id,
                color: color,
                name: nameString,
                multiple: productIds.size > 1,
              };

              if (productGroup) {
                productGroupContext = {
                  name: productGroup.name,
                  color: productGroup.color,
                };
              }
            }
          }

          const bulk = storageArea.bulkStorageData;
          if (bulk) {
            const allowedProducts: Product[] = [];

            for (const productId of bulk.allowedProducts) {
              const product = productStore.getById(productId);
              if (!product) continue;
              allowedProducts.push(product);
            }

            allowedProducts.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true }),
            );
            allowedProductsString = allowedProducts.map((p) => p.name).join(', ');

            if (bulk.disableAutomaticProductChange) {
              nameString = allowedProductsString;
            } else {
              nameString = products.map((p) => p.name).join(', ');
            }

            if (productContext) {
              productContext.name = nameString;
            }

            bulkStorageContext = {
              allowMultipleProducts: bulk.allowMultipleProducts,
              disableAutomaticProductChange: bulk.disableAutomaticProductChange,
              allowedProducts: allowedProductsString,
              content: products.map((p) => p.name).join(', '),
              firstMoveToDate: languageStore.formatMonthDay(bulk.firstMoveToUtc),
              lastEmptyDate: languageStore.formatMonthDay(bulk.lastEmptyUtc),
              lastInventoryDate: languageStore.formatMonthDay(bulk.lastInventoryUtc),
              firstMoveToDateTime: languageStore.formatDateTime(bulk.firstMoveToUtc),
              lastEmptyDateTime: languageStore.formatDateTime(bulk.lastEmptyUtc),
              lastInventoryDateTime: languageStore.formatDateTime(bulk.lastInventoryUtc),
              daysSinceFirstMove: getDaysSinceDate(bulk.firstMoveToUtc),
            };
          }

          const logYard = storageArea.logYardData;

          if (logYard) {
            const allowedProducts: Product[] = [];

            for (const productId of logYard.allowedProducts) {
              const product = productStore.getById(productId);
              if (!product) continue;
              allowedProducts.push(product);
            }

            allowedProducts.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true }),
            );
            allowedProductsString = allowedProducts.map((p) => p.name).join(', ');

            if (logYard.disableAutomaticProductChange) {
              nameString = allowedProductsString;
            } else {
              nameString = products.map((p) => p.name).join(', ');
            }

            if (productContext) {
              productContext.name = nameString;
            }

            logYardContext = {
              allowMultipleProducts: logYard.allowMultipleProducts,
              disableAutomaticProductChange: logYard.disableAutomaticProductChange,
              allowedProducts: allowedProductsString,
              content: products.map((p) => p.name).join(', '),
              firstMoveToDate: languageStore.formatMonthDay(logYard.firstMoveToUtc),
              lastEmptyDate: languageStore.formatMonthDay(logYard.lastEmptyUtc),
              lastInventoryDate: languageStore.formatMonthDay(logYard.lastInventoryUtc),
              firstMoveToDateTime: languageStore.formatDateTime(logYard.firstMoveToUtc),
              lastEmptyDateTime: languageStore.formatDateTime(logYard.lastEmptyUtc),
              lastInventoryDateTime: languageStore.formatDateTime(logYard.lastInventoryUtc),
              daysSinceFirstMove: getDaysSinceDate(logYard.firstMoveToUtc),
              sameProductAsLogSorterProductionOrderFunc: sameProductAsLogSorterProductionOrder(
                storageArea,
                logYard,
              ),
            };
          }
        }

        const context: ShapeContext = {
          vehicle: {
            empty: (vehicleLogYardContent === null || vehicleLogYardContent.length === 0 ) && (vehicleBulkContent === null || vehicleBulkContent.length === 0 ),
          },
          storageArea: storageAreaContext,
          product: productContext,
          storageAreaType: storageAreaTypes,
          storageAreaStatus: storageAreaStatuses,
          inside: storageArea?.id === insideStorageAreaId,
          selected: selected,
          logYard: logYardContext,
          bulk: bulkStorageContext,
          productGroup: productGroupContext,
          color: shape.color,
        };

        newColor = shapeStyle.color(context);
        newOutlineColor = shapeStyle.outlineColor(context);
        newOutlineSize = shapeStyle.outlineSize(context);
        newLabel = shapeStyle.label(context);
        newFontSize = shapeStyle.fontSize(context);
        messageIconVisible = shapeStyle.messageIconVisible(context);
      }

      const newTextColor = calculateTextColor(newColor);

      feature.setProperties({
        label: newLabel,
        color: newColor,
        outlineColor: newOutlineColor,
        outlineSize: newOutlineSize,
        opacity: newOpacity,
        fontSize: newFontSize,
        textColor: newTextColor,
        messageIconVisible: messageIconVisible,
      });

      viewModel.renderScheduled = false;
    }
  }
}

function createLayer(map: OLMap) {
  const source = new VectorSource();

  const polygonLayer = new VectorLayer({
    source: source,
    updateWhileAnimating: true,
    updateWhileInteracting: true,
    style: (feature) => {
      const properties = feature.getProperties();
      const label = properties['label'];
      const color = properties['color'];
      const opacity = properties['opacity'];
      const outlineColor = properties['outlineColor'];
      const outlineSize = properties['outlineSize'];
      const baseFontSize = properties['fontSize'] || 12;
      const textColor = properties['textColor'];
      const mode = properties['mode'];

      if (mode !== 'polygon') return;

      const resolution = map.getView().getResolution() || 1;
      let scaledFontSize = baseFontSize / resolution;

      scaledFontSize = Math.max(baseFontSize, scaledFontSize);

      let bold = false;

      const richText = parseRichText(label);
      const text: string[] = [];

      for (const token of richText.tokens) {
        switch (token.type) {
          case 'bold-start':
            bold = true;
            break;
          case 'bold-end':
            bold = false;
            break;
          case 'text':
            text.push(token.content);
            text.push(`${bold ? 'bold' : ''} ${scaledFontSize}px sans-serif`);
            break;
        }
      }

      return new Style({
        fill: new Fill({
          color: Color.setAlpha(color, opacity),
        }),
        stroke: new Stroke({
          color: Color.setAlpha(outlineColor, opacity),
          width: outlineSize,
        }),
        text: new Text({
          text: text,
          font: `${scaledFontSize}px sans-serif`,
          fill: new Fill({
            color: Color.setAlpha(textColor, opacity),
          }),
        }),
      });
    },
  });

  const rectangleLayer = new VectorLayer({
    source: source,
    style: rectangleStyle,
    updateWhileAnimating: true,
    updateWhileInteracting: true,
  });

  map.addLayer(polygonLayer);
  map.addLayer(rectangleLayer);

  return {
    source,
  };
}

interface ShapeViewModel {
  id: string;

  shape: Shape;

  renderScheduled: boolean;
  deleteScheduled: boolean;

  feature: Feature<Geometry> | null;
}
